import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { partRequestsAPI, purchaseOrdersAPI, warehouseAPI, crmJobsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Loader2, Edit2, Trash2, ClipboardList, Filter, Search, 
  CheckCircle2, XCircle, ShoppingCart, ArrowRight, Warehouse, Tag, Briefcase, Package,
  Hammer, Undo2, Info, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ItemSearchCombobox } from '../components/ItemSearchCombobox';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { AvailabilityBreakdownModal, useAvailabilityBreakdown } from '../components/AvailabilityBreakdownModal';
import { StockRowChoice, getStockRowOptions, apiErrorText } from '../components/StockRowChoice';

const STATUS_COLORS = {
  'Requested': 'bg-yellow-100 text-yellow-800',
  'Approved': 'bg-blue-100 text-blue-800',
  'Staged': 'bg-purple-100 text-purple-800',  // Phase 6B
  'Fulfilled': 'bg-green-100 text-green-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

const STATUS_OPTIONS = ['Requested', 'Approved', 'Staged', 'Fulfilled', 'Cancelled'];

export const PartRequests = () => {
  const { user, isOwner, isOffice, isTech, hasPerm } = useAuth();
  const navigate = useNavigate();
  const canApprove = hasPerm('po.approve');
  const canCreateRequest = hasPerm('parts.requests.create');
  const canDeleteRequest = hasPerm('parts.requests.delete');
  const [loading, setLoading] = useState(true);
  const [partRequests, setPartRequests] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  
  // Form state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    warehouse_id: '',
    department_id: '',
    job_id: null,
    job_number: '',
    job_name: '',
    customer_name_text: '',
    notes: '',
    line_items: [{ item_id: null, item_name: '', quantity_requested: 1, estimated_unit_cost: '' }],
  });
  const [saving, setSaving] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  
  // View details modal
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  
  // Cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteUnderstood, setDeleteUnderstood] = useState(false);
  
  // Convert to PO modal
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertingRequest, setConvertingRequest] = useState(null);
  
  // Phase 6B: Stage Parts modal
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [stagingRequest, setStagingRequest] = useState(null);
  const [stagingQuantities, setStagingQuantities] = useState({});
  const [stagingSaving, setStagingSaving] = useState(false);

  // Unstage modal (reverse staging operation)
  const [unstageModalOpen, setUnstageModalOpen] = useState(false);
  const [unstagingRequest, setUnstagingRequest] = useState(null);
  const [unstageReason, setUnstageReason] = useState('staged_by_mistake');
  const [unstageOtherText, setUnstageOtherText] = useState('');
  const [unstaging, setUnstaging] = useState(false);

  // PHASE 3A: Fulfill from Inventory modal
  const [fulfillModalOpen, setFulfillModalOpen] = useState(false);
  const [fulfillingRequest, setFulfillingRequest] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [fulfillQty, setFulfillQty] = useState({});
  const [fulfillRowChoice, setFulfillRowChoice] = useState({ lineId: null, options: null, rowId: null });
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [fulfilling, setFulfilling] = useState(false);
  const [breakdownState, openBreakdown, setBreakdownOpen] = useAvailabilityBreakdown();

  // CONSUMPTION: Mark Parts Used modal
  const [consumeModalOpen, setConsumeModalOpen] = useState(false);
  const [consumingRequest, setConsumingRequest] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [consumeQuantities, setConsumeQuantities] = useState({});
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [consuming, setConsuming] = useState(false);

  // RETURN TO INVENTORY modal
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returningRequest, setReturningRequest] = useState(null);
  const [returnAllocations, setReturnAllocations] = useState([]);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [returnReason, setReturnReason] = useState('');
  const [loadingReturnAllocations, setLoadingReturnAllocations] = useState(false);
  const [returning, setReturning] = useState(false);

  // Job search
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [jobSearchResults, setJobSearchResults] = useState([]);
  const [jobSearching, setJobSearching] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearchOpen, setJobSearchOpen] = useState(false);

  // Fetch data — hoisted so consume / return / other actions can refresh the
  // list. Without this, calling `fetchPartRequests()` after a successful
  // consume/return threw a ReferenceError *inside* the try block, firing the
  // generic "Failed to ..." toast even though the backend had already
  // committed and the row already reflected the new state.
  //
  // Refresh failures are intentionally swallowed (logged only) so they cannot
  // overshadow the success/failure toast of the upstream action. The initial
  // mount loader below catches its own error and surfaces a list-load toast.
  const fetchPartRequests = useCallback(async ({ silent = true } = {}) => {
    try {
      const [requestsRes, warehousesRes, departmentsRes] = await Promise.all([
        partRequestsAPI.list(),
        warehouseAPI.listWarehouses(),
        purchaseOrdersAPI.listDepartments(),
      ]);
      setPartRequests(requestsRes.data);
      setWarehouses(warehousesRes.data || []);
      setDepartments(departmentsRes.data || []);
    } catch (error) {
      console.error('Error refreshing part requests list:', error);
      if (!silent) {
        toast.error('Failed to load part requests');
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await fetchPartRequests({ silent: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPartRequests]);

  // Filter logic
  const filteredRequests = useMemo(() => {
    return partRequests.filter(pr => {
      if (statusFilter !== 'all' && pr.status !== statusFilter) return false;
      if (warehouseFilter !== 'all' && pr.warehouse_id !== warehouseFilter) return false;
      if (departmentFilter !== 'all' && pr.department_id !== departmentFilter) return false;
      if (searchFilter) {
        const search = searchFilter.toLowerCase();
        const jobMatch = pr.job_number?.toLowerCase().includes(search) || pr.job_name?.toLowerCase().includes(search);
        const requesterMatch = pr.requested_by_name?.toLowerCase().includes(search);
        if (!jobMatch && !requesterMatch) return false;
      }
      return true;
    });
  }, [partRequests, statusFilter, warehouseFilter, departmentFilter, searchFilter]);

  // Count by status
  const statusCounts = useMemo(() => {
    const counts = { Requested: 0, Approved: 0, Staged: 0, Fulfilled: 0, Cancelled: 0 };
    partRequests.forEach(pr => {
      if (counts[pr.status] !== undefined) counts[pr.status]++;
    });
    return counts;
  }, [partRequests]);

  // Reset form
  const resetForm = () => {
    setFormData({
      warehouse_id: warehouses.length > 0 ? warehouses[0].id : '',
      department_id: '',
      job_id: null,
      job_number: '',
      job_name: '',
      customer_name_text: '',
      notes: '',
      line_items: [{ item_id: null, item_name: '', quantity_requested: 1, estimated_unit_cost: '' }],
    });
    setSelectedJob(null);
    setJobSearchQuery('');
    setJobSearchResults([]);
  };

  // Open create modal
  const openCreateModal = () => {
    resetForm();
    if (warehouses.length > 0) {
      setFormData(prev => ({ ...prev, warehouse_id: warehouses[0].id }));
    }
    setCreateModalOpen(true);
  };

  // Handle line item change
  const handleLineItemChange = (index, field, value, isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const setData = isEdit ? setEditFormData : setFormData;
    
    const newLineItems = [...data.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setData({ ...data, line_items: newLineItems });
  };

  // Handle item selection from combobox
  const handleItemSelect = (index, item, isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const setData = isEdit ? setEditFormData : setFormData;
    
    const newLineItems = [...data.line_items];
    if (item) {
      newLineItems[index] = {
        ...newLineItems[index],
        item_id: item.item_id ?? item.id ?? null,
        item_name: item.item_name || item.name || item.part_number || '',
        estimated_unit_cost: item.unit_cost || newLineItems[index].estimated_unit_cost || ''
      };
    } else {
      newLineItems[index] = {
        ...newLineItems[index],
        item_id: null
      };
    }
    setData({ ...data, line_items: newLineItems });
  };

  // Add line item
  const addLineItem = (isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const setData = isEdit ? setEditFormData : setFormData;
    setData({
      ...data,
      line_items: [...data.line_items, { item_id: null, item_name: '', quantity_requested: 1, estimated_unit_cost: '' }]
    });
  };

  // Remove line item
  const removeLineItem = (index, isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const setData = isEdit ? setEditFormData : setFormData;
    if (data.line_items.length > 1) {
      const newLineItems = data.line_items.filter((_, i) => i !== index);
      setData({ ...data, line_items: newLineItems });
    }
  };

  // Create request
  const handleCreate = async () => {
    // Validation
    if (!formData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }
    const validLineItems = formData.line_items.filter(li => li.item_name.trim());
    if (validLineItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        warehouse_id: formData.warehouse_id,
        department_id: formData.department_id || null,
        job_id: formData.job_id || null,
        job_number: formData.job_number || null,
        job_name: formData.job_name || null,
        notes: formData.notes || null,
        line_items: validLineItems.map(li => ({
          item_id: li.item_id || null,
          item_name: li.item_name,
          quantity_requested: parseInt(li.quantity_requested) || 1,
          estimated_unit_cost: li.estimated_unit_cost ? parseFloat(li.estimated_unit_cost) : null
        }))
      };

      const response = await partRequestsAPI.create(payload);
      setPartRequests([response.data, ...partRequests]);
      setCreateModalOpen(false);
      toast.success('Part request created successfully');
    } catch (error) {
      console.error('Error creating part request:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to create part requests.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create part request');
      }
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal
  const openEditModal = async (request) => {
    try {
      const response = await partRequestsAPI.get(request.id);
      setEditingRequest(response.data);
      setEditFormData({
        warehouse_id: response.data.warehouse_id,
        department_id: response.data.department_id || '',
        job_number: response.data.job_number || '',
        job_name: response.data.job_name || '',
        notes: response.data.notes || '',
        line_items: response.data.line_items.map(li => ({
          id: li.id,
          item_id: li.item_id,
          item_name: li.item_name,
          quantity_requested: li.quantity_requested,
          estimated_unit_cost: li.estimated_unit_cost || ''
        }))
      });
      setEditModalOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  // Update request
  const handleUpdate = async () => {
    if (!editFormData.warehouse_id) {
      toast.error('Please select a warehouse');
      return;
    }
    const validLineItems = editFormData.line_items.filter(li => li.item_name.trim());
    if (validLineItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setEditSaving(true);
    try {
      const payload = {
        warehouse_id: editFormData.warehouse_id,
        department_id: editFormData.department_id || null,
        job_number: editFormData.job_number || null,
        job_name: editFormData.job_name || null,
        notes: editFormData.notes || null,
        line_items: validLineItems.map(li => ({
          id: li.id || null,
          item_id: li.item_id || null,
          item_name: li.item_name,
          quantity_requested: parseInt(li.quantity_requested) || 1,
          estimated_unit_cost: li.estimated_unit_cost ? parseFloat(li.estimated_unit_cost) : null
        }))
      };

      const response = await partRequestsAPI.update(editingRequest.id, payload);
      setPartRequests(partRequests.map(pr => pr.id === editingRequest.id ? response.data : pr));
      setEditModalOpen(false);
      toast.success('Part request updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setEditSaving(false);
    }
  };

  // View request details
  const viewRequestDetails = async (request) => {
    try {
      const response = await partRequestsAPI.get(request.id);
      setViewingRequest(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  // Approve request
  const handleApprove = async (request) => {
    try {
      const response = await partRequestsAPI.approve(request.id);
      setPartRequests(partRequests.map(pr => pr.id === request.id ? response.data : pr));
      if (viewingRequest?.id === request.id) {
        setViewingRequest(response.data);
      }
      toast.success('Part request approved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  // Cancel request
  const openCancelModal = (request) => {
    setCancellingRequest(request);
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const handleCancel = async () => {
    setCancelSaving(true);
    try {
      const response = await partRequestsAPI.cancel(cancellingRequest.id, {
        status: 'Cancelled',
        cancellation_reason: cancelReason || null
      });
      setPartRequests(partRequests.map(pr => pr.id === cancellingRequest.id ? response.data : pr));
      if (viewingRequest?.id === cancellingRequest.id) {
        setViewingRequest(response.data);
      }
      setCancelModalOpen(false);
      toast.success('Part request cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setCancelSaving(false);
    }
  };

  // Delete request
  const openDeleteConfirm = (request) => {
    setDeletingRequest(request);
    setDeleteUnderstood(false);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await partRequestsAPI.delete(deletingRequest.id);
      setPartRequests(partRequests.filter(pr => pr.id !== deletingRequest.id));
      setDeleteConfirmOpen(false);
      setViewModalOpen(false);
      toast.success('Part request deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    } finally {
      setDeleting(false);
    }
  };

  // Convert to PO
  const openConvertModal = (request) => {
    setConvertingRequest(request);
    setConvertModalOpen(true);
  };

  const handleConvertToPO = async () => {
    try {
      const response = await partRequestsAPI.convertToPO(convertingRequest.id);
      const poData = response.data;
      
      // Update the request in the list to show as Fulfilled
      const updatedResponse = await partRequestsAPI.get(convertingRequest.id);
      setPartRequests(partRequests.map(pr => 
        pr.id === convertingRequest.id ? { ...pr, status: 'Fulfilled' } : pr
      ));
      
      setConvertModalOpen(false);
      
      // Navigate to PurchaseOrders page with pre-filled data in session storage
      sessionStorage.setItem('prefill_po', JSON.stringify(poData));
      navigate('/purchase-orders?prefill=true');
      
      toast.success('Part request fulfilled. Creating Purchase Order...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to PO');
    }
  };

  // PHASE 3A: Fulfill from Inventory
  const openFulfillModal = async (request) => {
    setFulfillingRequest(request);
    setAvailability(null);
    setFulfillQty({});
    setFulfillRowChoice({ lineId: null, options: null, rowId: null });
    setFulfillModalOpen(true);
    setCheckingAvailability(true);
    
    try {
      const response = await partRequestsAPI.checkAvailability(request.id);
      setAvailability(response.data);
      setFulfillQty(Object.fromEntries(response.data.items
        .filter((i) => i.item_id && i.outstanding > 0)
        .map((i) => [i.line_item_id, Math.min(i.outstanding, i.available_quantity)])));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check inventory availability');
      setFulfillModalOpen(false);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleFulfillFromInventory = async () => {
    if (!fulfillingRequest) return;
    setFulfilling(true);
    
    try {
      const items = Object.entries(fulfillQty).map(([line_item_id, quantity]) => ({
        line_item_id, quantity: quantity || 0,
        warehouse_inventory_id: fulfillRowChoice.lineId === line_item_id ? fulfillRowChoice.rowId : null,
      }));
      const response = await partRequestsAPI.fulfillFromInventory(fulfillingRequest.id, { items });
      const result = response.data;
      
      // Update the request in the list
      setPartRequests(partRequests.map(pr => 
        pr.id === fulfillingRequest.id
          ? { ...pr, status: result.status || 'Fulfilled', has_fulfilled_quantity: true }
          : pr
      ));
      
      setFulfillModalOpen(false);
      
      if (result.email_sent) {
        toast.success(`${result.message}. Email notification sent.`);
      } else {
        toast.success(result.message);
        if (result.email_reason && result.email_reason !== 'no_recipients_configured') {
          toast.info(`Email not sent: ${result.email_reason}`);
        }
      }
    } catch (error) {
      const options = getStockRowOptions(error);
      if (options) {
        setFulfillRowChoice({ lineId: error.response.data.detail.line_item_id, options, rowId: null });
      }
      toast.error(apiErrorText(error, 'Failed to fulfill from inventory'));
    } finally {
      setFulfilling(false);
    }
  };

  const fulfillItems = availability?.items.filter((i) => i.item_id && i.outstanding > 0) || [];
  const fulfillTotal = fulfillItems.reduce((n, i) => n + (fulfillQty[i.line_item_id] || 0), 0);
  const fulfillValid = fulfillTotal > 0 && fulfillItems.every((i) => (fulfillQty[i.line_item_id] || 0) <= Math.min(i.outstanding, i.available_quantity))
    && !availability?.items.some((i) => !i.item_id && i.outstanding > 0)
    && (!fulfillRowChoice.options || fulfillRowChoice.rowId);


  // ─── CONSUMPTION: Mark Parts Used ───
  const openConsumeModal = async (request) => {
    setLoadingAllocations(true);
    setConsumingRequest(request);
    setConsumeModalOpen(true);
    try {
      const res = await partRequestsAPI.getAllocations(request.id);
      const allocs = res.data.allocations || [];
      setAllocations(allocs);
      const initial = {};
      allocs.forEach(a => { initial[a.item_id] = a.qty_remaining; });
      setConsumeQuantities(initial);
    } catch (e) {
      toast.error('Failed to load allocations');
      setConsumeModalOpen(false);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const handleConsume = async () => {
    if (!consumingRequest) return;
    setConsuming(true);
    try {
      const items = allocations
        .filter(a => (consumeQuantities[a.item_id] || 0) > 0)
        .map(a => ({ item_id: a.item_id, quantity_used: consumeQuantities[a.item_id] || 0 }));
      if (items.length === 0) { toast.error('No items selected'); setConsuming(false); return; }
      const res = await partRequestsAPI.consume(consumingRequest.id, { items });
      toast.success(res.data.message);
      // Update local state so row reflects consumed status immediately
      if (res.data.all_consumed) {
        setPartRequests(prev => prev.map(pr =>
          pr.id === consumingRequest.id ? { ...pr, fully_consumed: true } : pr
        ));
      }
      setConsumeModalOpen(false);
      fetchPartRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to consume parts');
    } finally {
      setConsuming(false);
    }
  };

  // ─── RETURN TO INVENTORY ───
  const openReturnModal = async (request) => {
    setLoadingReturnAllocations(true);
    setReturningRequest(request);
    setReturnModalOpen(true);
    setReturnReason('');
    try {
      const res = await partRequestsAPI.getAllocations(request.id);
      const allocs = (res.data.allocations || []).filter(a => (a.qty_remaining > 0 || a.qty_used > 0));
      setReturnAllocations(allocs);
      const initial = {};
      allocs.forEach(a => { initial[a.item_id] = 0; });
      setReturnQuantities(initial);
    } catch (e) {
      toast.error('Failed to load allocations');
      setReturnModalOpen(false);
    } finally {
      setLoadingReturnAllocations(false);
    }
  };

  const handleReturn = async () => {
    if (!returningRequest) return;
    setReturning(true);
    try {
      const items = returnAllocations
        .filter(a => (returnQuantities[a.item_id] || 0) > 0)
        .map(a => ({ item_id: a.item_id, quantity_to_return: returnQuantities[a.item_id] || 0 }));
      if (items.length === 0) { toast.error('No items selected for return'); setReturning(false); return; }
      const res = await partRequestsAPI.returnToInventory(returningRequest.id, { items, reason: returnReason || null });
      toast.success(res.data.message);
      setReturnModalOpen(false);
      fetchPartRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to return parts');
    } finally {
      setReturning(false);
    }
  };

  // ─── JOB SEARCH ───
  const searchJobs = useCallback(async (query) => {
    if (!query || query.length < 2) { setJobSearchResults([]); return; }
    setJobSearching(true);
    try {
      const res = await crmJobsAPI.list({ search: query, limit: 10 });
      setJobSearchResults(res.data.jobs || []);
    } catch { setJobSearchResults([]); }
    finally { setJobSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchJobs(jobSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [jobSearchQuery, searchJobs]);

  // Phase 6B: Stage Parts
  const openStageModal = async (request) => {
    try {
      // Fetch full request details with line items
      const response = await partRequestsAPI.get(request.id);
      const fullRequest = response.data;
      setStagingRequest(fullRequest);
      
      // Initialize staging quantities from current values
      const initialQuantities = {};
      fullRequest.line_items.forEach(item => {
        initialQuantities[item.id] = item.quantity_staged || 0;
      });
      setStagingQuantities(initialQuantities);
      setStageModalOpen(true);
    } catch (error) {
      toast.error('Failed to load request details');
    }
  };

  const handleStagingQuantityChange = (itemId, value) => {
    setStagingQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, parseInt(value) || 0)
    }));
  };

  const handleStageAll = () => {
    if (!stagingRequest) return;
    const allStaged = {};
    stagingRequest.line_items.forEach(item => {
      allStaged[item.id] = item.quantity_requested;
    });
    setStagingQuantities(allStaged);
  };

  const handleSaveStaging = async () => {
    if (!stagingRequest) return;
    setStagingSaving(true);
    
    try {
      const items = Object.entries(stagingQuantities).map(([item_id, quantity_staged]) => ({
        item_id,
        quantity_staged
      }));
      
      const response = await partRequestsAPI.stage(stagingRequest.id, { items });
      const { part_request, all_staged, job_ready_to_schedule } = response.data;
      
      // Update the request in the list
      setPartRequests(partRequests.map(pr => 
        pr.id === stagingRequest.id 
          ? { ...pr, status: part_request.status }
          : pr
      ));
      
      setStageModalOpen(false);
      
      if (job_ready_to_schedule) {
        toast.success(`Parts staged! Job ${part_request.job_number} is now READY TO SCHEDULE.`, {
          duration: 5000,
        });
      } else if (all_staged) {
        toast.success('All parts staged successfully!');
      } else {
        toast.success('Staging progress saved');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stage parts');
    } finally {
      setStagingSaving(false);
    }
  };

  // Unstage: reverse staging on a part request
  const openUnstageModal = async (request) => {
    try {
      // Fetch full details (so the confirmation can show the line quantities
      // that are about to be cleared) — falls back gracefully if the call
      // fails: the user can still confirm and the backend will roll back if
      // something is amiss.
      const response = await partRequestsAPI.get(request.id);
      setUnstagingRequest(response.data);
    } catch (_err) {
      setUnstagingRequest(request);
    }
    setUnstageReason('staged_by_mistake');
    setUnstageOtherText('');
    setUnstageModalOpen(true);
  };

  const handleConfirmUnstage = async () => {
    if (!unstagingRequest) return;
    setUnstaging(true);
    try {
      const body = { reason: unstageReason };
      if (unstageReason === 'other' && unstageOtherText.trim()) {
        body.reason_other_text = unstageOtherText.trim();
      }
      const res = await partRequestsAPI.unstage(unstagingRequest.id, body);
      const restored = res.data?.restored_status || 'Approved';
      const cleared = res.data?.lines_cleared ?? 0;
      // Refresh local list status + clear has_staged_quantity flag for this row.
      setPartRequests((prev) => prev.map((pr) =>
        pr.id === unstagingRequest.id
          ? { ...pr, status: restored.charAt(0).toUpperCase() + restored.slice(1), has_staged_quantity: false }
          : pr
      ));
      setUnstageModalOpen(false);
      setUnstagingRequest(null);
      toast.success(`Unstaged - ${cleared} line(s) cleared; restored to ${restored}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unstage');
    } finally {
      setUnstaging(false);
    }
  };



  // Render line items inline (NOT as a component — avoids re-mounting on state change)
  const renderLineItems = (lineItems, isEdit) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Items</Label>
        <Button type="button" variant="outline" size="sm" onClick={() => addLineItem(isEdit)}>
          <Plus className="w-4 h-4 mr-1" /> Add Item
        </Button>
      </div>
      {lineItems.map((item, index) => (
        <div key={item.item_id || `line-${index}`} className="grid grid-cols-12 gap-2 items-end p-3 border rounded-md bg-muted/30">
          <div className="col-span-12 md:col-span-5">
            <Label className="text-xs">Item (search or type)</Label>
            <ItemSearchCombobox
              value={item.item_name}
              itemId={item.item_id}
              onChange={(newValue) => handleLineItemChange(index, 'item_name', newValue, isEdit)}
              onSelect={(selectedItem) => handleItemSelect(index, selectedItem, isEdit)}
              placeholder="Search inventory..."
              className="mt-1"
            />
          </div>
          <div className="col-span-5 md:col-span-3">
            <Label className="text-xs">Quantity</Label>
            <IntegerQuantityInput
              min="1"
              value={item.quantity_requested}
              onChange={(val) => handleLineItemChange(index, 'quantity_requested', val, isEdit)}
              className="mt-1"
              data-testid={`part-request-qty-${index}`}
            />
          </div>
          <div className="col-span-5 md:col-span-3">
            <Label className="text-xs">Est. Cost</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={item.estimated_unit_cost}
              onChange={(e) => handleLineItemChange(index, 'estimated_unit_cost', e.target.value, isEdit)}
              placeholder="$0.00"
              className="mt-1"
            />
          </div>
          <div className="col-span-2 md:col-span-1">
            {lineItems.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeLineItem(index, isEdit)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="part-requests-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="w-6 h-6" />
              Part Requests
            </h1>
            <p className="text-muted-foreground text-sm mt-1 hidden sm:block">
              Request parts from purchasing for jobs
            </p>
          </div>
          {canCreateRequest && (
          <Button onClick={openCreateModal} data-testid="create-part-request-btn" className="hidden md:inline-flex">
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
          )}
        </div>

        {/* Status Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATUS_OPTIONS.map(status => (
            <Card 
              key={status} 
              className={`cursor-pointer transition-all ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{status}</span>
                  <Badge className={STATUS_COLORS[status]}>{statusCounts[status]}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by job number, name, or requester..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="pl-10"
                    data-testid="part-requests-search"
                  />
                </div>
                {/* Mobile filters toggle */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 relative md:hidden"
                  onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                  aria-label="Filters"
                >
                  <Filter className="w-4 h-4" />
                  {(warehouseFilter !== 'all' || departmentFilter !== 'all' || statusFilter !== 'all') && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary text-primary-foreground rounded-full flex items-center justify-center">!</span>
                  )}
                </Button>
              </div>
              {/* Desktop: inline filters */}
              <div className="hidden md:flex gap-2 flex-wrap">
                <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="warehouse-filter">
                    <Warehouse className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {departments.length > 0 && (
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="department-filter">
                      <Tag className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {statusFilter !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>
                    Clear Status Filter
                  </Button>
                )}
              </div>
            </div>
            {/* Mobile: expanded filters */}
            {mobileFiltersOpen && (
              <div className="md:hidden mt-3 flex flex-col gap-3 pt-3 border-t">
                <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                  <SelectTrigger data-testid="warehouse-filter-mobile">
                    <Warehouse className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {departments.length > 0 && (
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger data-testid="department-filter-mobile">
                      <Tag className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {statusFilter !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>
                    Clear Status Filter
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Requests List */}
        <Card>
          <CardContent className="p-0">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No part requests found</p>
                {(statusFilter !== 'all' || warehouseFilter !== 'all' || searchFilter) && (
                  <Button 
                    variant="link" 
                    onClick={() => {
                      setStatusFilter('all');
                      setWarehouseFilter('all');
                      setDepartmentFilter('all');
                      setSearchFilter('');
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y">
                {filteredRequests.map(request => (
                  <div 
                    key={request.id} 
                    className="p-4 hover:bg-muted/50 cursor-pointer"
                    onClick={() => viewRequestDetails(request)}
                    data-testid={`part-request-row-${request.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={STATUS_COLORS[request.status]}>{request.status}</Badge>
                          {request.has_fulfilled_quantity && request.status !== 'Fulfilled' && request.status !== 'Cancelled' && (
                            <Badge variant="outline" className="text-xs border-green-300 text-green-700" data-testid={`partly-fulfilled-badge-${request.id}`}>
                              Partly fulfilled
                            </Badge>
                          )}
                          {request.job_number && (
                            <span className="font-medium text-sm flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {request.job_number}
                            </span>
                          )}
                          {request.department_name && (
                            <Badge variant="outline" className="text-xs">
                              <Tag className="w-3 h-3 mr-1" />
                              {request.department_name}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {request.job_name && <span className="mr-2">{request.job_name}</span>}
                          <span>• {request.warehouse_name}</span>
                          <span className="mx-2">•</span>
                          <span>{request.line_item_count} item{request.line_item_count !== 1 ? 's' : ''}</span>
                          {request.total_estimated_cost && (
                            <span className="ml-2">• ~${request.total_estimated_cost.toFixed(2)}</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Requested by {request.requested_by_name} • {format(new Date(request.created_at), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {request.status === 'Requested' && canApprove && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleApprove(request)} data-testid={`approve-btn-${request.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openCancelModal(request)}>
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {request.status === 'Approved' && canApprove && (
                          <>
                            <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => openFulfillModal(request)} data-testid={`fulfill-btn-${request.id}`}>
                              <Warehouse className="w-4 h-4 mr-1" />
                              Fulfill from Stock
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openStageModal(request)} data-testid={`stage-btn-${request.id}`}>
                              <Package className="w-4 h-4 mr-1" />
                              Stage Parts
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openConvertModal(request)} data-testid={`convert-btn-${request.id}`}>
                              <ShoppingCart className="w-4 h-4 mr-1" />
                              Create PO
                            </Button>
                          </>
                        )}
                        {request.status === 'Approved' && canApprove && request.has_staged_quantity && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openUnstageModal(request)}
                            data-testid={`unstage-btn-${request.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Clear Staging
                          </Button>
                        )}
                        {request.status === 'Staged' && canApprove && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openUnstageModal(request)}
                            data-testid={`unstage-btn-${request.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Unstage
                          </Button>
                        )}
                        {request.status === 'Staged' && canApprove && (
                          <Button size="sm" onClick={() => openConvertModal(request)} data-testid={`convert-btn-${request.id}`}>
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            Convert to PO
                          </Button>
                        )}
                        {request.status === 'Staged' && canApprove && (
                          <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700" onClick={() => openFulfillModal(request)} data-testid={`fulfill-staged-btn-${request.id}`}>
                            <Warehouse className="w-4 h-4 mr-1" />
                            Fulfill from Stock
                          </Button>
                        )}
                        {request.has_fulfilled_quantity && request.status === 'Approved' && canApprove && (
                          <Button size="sm" variant="outline" onClick={() => openConsumeModal(request)} data-testid={`consume-partial-btn-${request.id}`}>
                            <Hammer className="w-4 h-4 mr-1" />
                            Mark Parts Used
                          </Button>
                        )}
                        {request.status === 'Fulfilled' && request.fulfillment_method !== 'via_po' && !request.fully_consumed && canApprove && (
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => openConsumeModal(request)}
                            data-testid={`consume-btn-${request.id}`}
                          >
                            <Hammer className="w-4 h-4 mr-1" />
                            Mark Parts Used
                          </Button>
                        )}
                        {request.status === 'Fulfilled' && request.fulfillment_method !== 'via_po' && request.fully_consumed && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-2.5 py-1 rounded-md" data-testid={`consumed-badge-${request.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Parts Used
                          </span>
                        )}
                        {request.status === 'Fulfilled' && request.fulfillment_method !== 'via_po' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReturnModal(request)}
                            data-testid={`return-btn-${request.id}`}
                          >
                            <Undo2 className="w-4 h-4 mr-1" />
                            Return to Inventory
                          </Button>
                        )}
                        {request.status === 'Staged' && request.fulfillment_method !== 'via_po' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReturnModal(request)}
                            data-testid={`return-btn-${request.id}`}
                          >
                            <Undo2 className="w-4 h-4 mr-1" />
                            Return to Inventory
                          </Button>
                        )}
                        {request.status === 'Fulfilled' && request.fulfillment_method === 'via_po' && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md" data-testid={`po-fulfilled-${request.id}`}>
                            Ordered via PO
                          </span>
                        )}
                        {request.status === 'Requested' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openEditModal(request)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {canDeleteRequest && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openDeleteConfirm(request)} data-testid={`delete-btn-${request.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        {/* New Part Request Drawer */}
        <AppDrawer
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          title="New Part Request"
          description="Request parts from purchasing for a job"
          size="md"
          isDirty={!!(formData.job_number || formData.job_name || formData.notes || formData.line_items?.some(li => li.item_name.trim()))}
          data-testid="create-part-request-drawer"
          footer={
            <>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="submit-part-request-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit Request
              </Button>
            </>
          }
        >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Warehouse *</Label>
                  <Select value={formData.warehouse_id} onValueChange={(v) => setFormData({...formData, warehouse_id: v})}>
                    <SelectTrigger data-testid="create-warehouse-select">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {departments.length > 0 && (
                  <div>
                    <Label>Department</Label>
                    <Select value={formData.department_id || "_none"} onValueChange={(v) => setFormData({...formData, department_id: v === "_none" ? "" : v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {departments.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Job (search or enter manually)</Label>
                  <div className="relative">
                    <Input
                      value={selectedJob ? `${selectedJob.job_number} — ${selectedJob.job_name || selectedJob.customer_name || ''}` : jobSearchQuery || formData.job_number || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSelectedJob(null);
                        setJobSearchQuery(v);
                        setJobSearchOpen(true);
                        setFormData({...formData, job_id: null, job_number: v, job_name: '', customer_name_text: ''});
                      }}
                      placeholder="Search by job # or customer name, or type manually..."
                      data-testid="create-job-search"
                    />
                    {selectedJob && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          setSelectedJob(null);
                          setJobSearchQuery('');
                          setFormData({...formData, job_id: null, job_number: '', job_name: '', customer_name_text: ''});
                        }}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    {jobSearchOpen && jobSearchResults.length > 0 && !selectedJob && (
                      <div className="absolute z-50 top-full mt-1 w-full bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {jobSearchResults.map(j => (
                          <button
                            key={j.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                            onClick={() => {
                              setSelectedJob(j);
                              setJobSearchOpen(false);
                              setJobSearchQuery('');
                              setFormData({
                                ...formData,
                                job_id: j.id,
                                job_number: j.job_number,
                                job_name: j.job_name || j.title || '',
                                customer_name_text: j.customer_name || '',
                              });
                            }}
                            data-testid={`job-option-${j.id}`}
                          >
                            <span className="font-medium">{j.job_number}</span>
                            {(j.job_name || j.title) && <span className="text-muted-foreground ml-2">— {j.job_name || j.title}</span>}
                            {j.customer_name && <span className="text-muted-foreground ml-2">({j.customer_name})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {jobSearchOpen && jobSearchQuery.length >= 2 && jobSearchResults.length === 0 && !jobSearching && !selectedJob && (
                      <div className="absolute z-50 top-full mt-1 w-full bg-background border rounded-md shadow-lg px-3 py-2 text-sm text-muted-foreground">
                        No jobs found — using as manual entry
                      </div>
                    )}
                    {jobSearching && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedJob ? 'Linked to CRM job' : 'Type to search existing jobs or enter a manual job reference'}
                  </p>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special instructions or details..."
                  rows={2}
                />
              </div>
              {renderLineItems(formData.line_items, false)}
            </div>
        </AppDrawer>

        {/* Edit Part Request Drawer */}
        <AppDrawer
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          title="Edit Part Request"
          description="Update the details of this part request"
          size="md"
          isDirty={false}
          data-testid="edit-part-request-drawer"
          footer={
            <>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={editSaving}>
                {editSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </>
          }
        >
            {editFormData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Warehouse *</Label>
                    <Select value={editFormData.warehouse_id} onValueChange={(v) => setEditFormData({...editFormData, warehouse_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {departments.length > 0 && (
                    <div>
                      <Label>Department</Label>
                      <Select value={editFormData.department_id || "_none"} onValueChange={(v) => setEditFormData({...editFormData, department_id: v === "_none" ? "" : v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None</SelectItem>
                          {departments.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Job Number</Label>
                    <Input
                      value={editFormData.job_number}
                      onChange={(e) => setEditFormData({...editFormData, job_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Job Name</Label>
                    <Input
                      value={editFormData.job_name}
                      onChange={(e) => setEditFormData({...editFormData, job_name: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    rows={2}
                  />
                </div>
                {renderLineItems(editFormData.line_items, true)}
              </div>
            )}
        </AppDrawer>

        {/* View Details Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Part Request Details</DialogTitle>
            </DialogHeader>
            {viewingRequest && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_COLORS[viewingRequest.status]}>{viewingRequest.status}</Badge>
                  {viewingRequest.job_number && (
                    <span className="font-medium">Job #{viewingRequest.job_number}</span>
                  )}
                  {viewingRequest.department_name && (
                    <Badge variant="outline">{viewingRequest.department_name}</Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Warehouse:</span>
                    <p className="font-medium">{viewingRequest.warehouse_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Requested By:</span>
                    <p className="font-medium">{viewingRequest.requested_by_name}</p>
                  </div>
                  {viewingRequest.job_name && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Job Name:</span>
                      <p className="font-medium">{viewingRequest.job_name}</p>
                    </div>
                  )}
                  {viewingRequest.customer_name_text && (
                    <div>
                      <span className="text-muted-foreground">Customer:</span>
                      <p className="font-medium">{viewingRequest.customer_name_text}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">{format(new Date(viewingRequest.created_at), 'MMM d, yyyy h:mm a')}</p>
                  </div>
                  {viewingRequest.approved_at && (
                    <div>
                      <span className="text-muted-foreground">Approved:</span>
                      <p className="font-medium">{format(new Date(viewingRequest.approved_at), 'MMM d, yyyy h:mm a')}</p>
                      <p className="text-xs text-muted-foreground">by {viewingRequest.approved_by_name}</p>
                    </div>
                  )}
                  {viewingRequest.fulfilled_at && (
                    <div>
                      <span className="text-muted-foreground">Fulfilled:</span>
                      <p className="font-medium">{format(new Date(viewingRequest.fulfilled_at), 'MMM d, yyyy h:mm a')}</p>
                      <p className="text-xs text-muted-foreground">by {viewingRequest.fulfilled_by_name}</p>
                    </div>
                  )}
                  {viewingRequest.cancelled_at && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Cancelled:</span>
                      <p className="font-medium">{format(new Date(viewingRequest.cancelled_at), 'MMM d, yyyy h:mm a')}</p>
                      <p className="text-xs text-muted-foreground">by {viewingRequest.cancelled_by_name}</p>
                      {viewingRequest.cancellation_reason && (
                        <p className="text-sm mt-1 p-2 bg-muted rounded">{viewingRequest.cancellation_reason}</p>
                      )}
                    </div>
                  )}
                </div>
                
                {viewingRequest.notes && (
                  <div>
                    <span className="text-sm text-muted-foreground">Notes:</span>
                    <p className="text-sm p-2 bg-muted rounded mt-1">{viewingRequest.notes}</p>
                  </div>
                )}
                
                <div>
                  <span className="text-sm font-medium">Line Items</span>
                  <div className="border rounded mt-2 divide-y">
                    {viewingRequest.line_items.map((li, idx) => (
                      <div key={li.id || idx} className="p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{li.item_name}</p>
                          {li.item_part_number && (
                            <p className="text-xs text-muted-foreground">Part #: {li.item_part_number}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p>Qty: {li.quantity_requested}</p>
                          {li.quantity_staged > 0 && (
                            <p className="text-xs text-purple-600">
                              Staged: {li.quantity_staged}/{li.quantity_requested}
                            </p>
                          )}
                          {li.estimated_unit_cost && (
                            <p className="text-sm text-muted-foreground">~${li.estimated_unit_cost.toFixed(2)} ea</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {viewingRequest.total_estimated_cost && (
                    <div className="text-right mt-2 font-medium">
                      Estimated Total: ${viewingRequest.total_estimated_cost.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              {canDeleteRequest && viewingRequest && (
                <Button variant="destructive" onClick={() => openDeleteConfirm(viewingRequest)} data-testid="detail-delete-btn" className="sm:mr-auto">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              )}
              {viewingRequest?.status === 'Requested' && canApprove && (
                <>
                  <Button variant="outline" onClick={() => openCancelModal(viewingRequest)}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                  <Button onClick={() => handleApprove(viewingRequest)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                </>
              )}
              {viewingRequest?.status === 'Approved' && canApprove && (
                <>
                  <Button variant="outline" onClick={() => { setViewModalOpen(false); openStageModal(viewingRequest); }}>
                    <Package className="w-4 h-4 mr-1" />
                    Stage Parts
                  </Button>
                  <Button onClick={() => { setViewModalOpen(false); openConvertModal(viewingRequest); }}>
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Convert to PO
                  </Button>
                </>
              )}
              {viewingRequest?.status === 'Staged' && canApprove && (
                <Button onClick={() => { setViewModalOpen(false); openConvertModal(viewingRequest); }}>
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Convert to PO
                </Button>
              )}
              <Button variant="outline" onClick={() => setViewModalOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Modal */}
        <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Part Request</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel this request? This will release any reserved inventory.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Reason for cancellation (optional)</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Enter reason..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelModalOpen(false)}>Back</Button>
              <Button variant="destructive" onClick={handleCancel} disabled={cancelSaving}>
                {cancelSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Cancel Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => { setDeleteConfirmOpen(open); if (!open) setDeleteUnderstood(false); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Part Request?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the part request{deletingRequest?.job_number ? ` for Job #${deletingRequest.job_number}` : ''} and all its line items. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex items-center gap-2 py-2">
              <input
                type="checkbox"
                id="delete-understood"
                checked={deleteUnderstood}
                onChange={(e) => setDeleteUnderstood(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="delete-understood-checkbox"
              />
              <label htmlFor="delete-understood" className="text-sm select-none cursor-pointer">
                I understand this cannot be undone
              </label>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting || !deleteUnderstood} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
                {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* PHASE 3A: Fulfill from Inventory Modal */}
        <Dialog open={fulfillModalOpen} onOpenChange={setFulfillModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Warehouse className="w-5 h-5 text-green-600" />
                Fulfill from Warehouse Stock
              </DialogTitle>
              <DialogDescription>
                Allocate parts from warehouse inventory to this job.
              </DialogDescription>
            </DialogHeader>

            {checkingAvailability ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Checking inventory...</span>
              </div>
            ) : availability ? (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="text-sm">
                    <span className="font-medium">Warehouse:</span> {availability.warehouse_name}
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-medium">Job:</span> {fulfillingRequest?.job_number} {fulfillingRequest?.job_name && `- ${fulfillingRequest.job_name}`}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Items to Allocate:</Label>
                  <div className="border rounded-lg divide-y">
                    {availability.items.map((item) => (
                      <div key={item.line_item_id} className="p-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{item.item_name}</div>
                          <div className="text-xs text-muted-foreground">
                            Requested: {item.quantity_requested}
                            {item.quantity_fulfilled > 0 && <> • Already assigned: {item.quantity_fulfilled}</>}
                            {' '}• Available: <span data-testid={`fulfill-available-${item.line_item_id}`}>{item.available_quantity}</span>
                            {item.reserved_for_request > 0 && <> (incl. {item.reserved_for_request} reserved for this request)</>}
                            {item.item_id && (
                              <button
                                type="button"
                                className="ml-1 inline-flex items-center text-primary hover:text-primary/80"
                                onClick={() => openBreakdown(item.item_id, 'warehouse', fulfillingRequest?.warehouse_id)}
                                title="View availability breakdown"
                                data-testid={`fulfill-breakdown-${item.item_id}`}
                              >
                                <Info className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="ml-3 flex items-center gap-2">
                          {item.item_id && item.outstanding > 0 && (
                            <Input type="number" min="0" max={Math.min(item.outstanding, item.available_quantity)}
                              value={fulfillQty[item.line_item_id] ?? 0}
                              onChange={(e) => setFulfillQty((q) => ({ ...q, [item.line_item_id]: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                              className="w-16 h-8 font-mono" data-testid={`fulfill-qty-${item.line_item_id}`} />
                          )}
                          {item.outstanding === 0 ? (
                            <Badge className="bg-slate-100 text-slate-700">Done</Badge>
                          ) : item.is_available ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              In Stock
                            </Badge>
                          ) : item.item_id ? (
                            <Badge variant="destructive">
                              Short {item.shortage}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Not Linked
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {fulfillRowChoice.options && (
                  <StockRowChoice options={fulfillRowChoice.options} value={fulfillRowChoice.rowId}
                    onChange={(rowId) => setFulfillRowChoice((c) => ({ ...c, rowId }))} />
                )}

                {fulfillValid && fulfillItems.some((i) => (fulfillQty[i.line_item_id] || 0) < i.outstanding) && (
                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800" data-testid="fulfill-partial-note">
                    Partial fulfillment: the request stays open and the rest stays reserved.
                  </div>
                )}

                {!availability.all_items_available && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>Note:</strong> Some items are not available in stock. You can:
                    <ul className="list-disc ml-5 mt-1">
                      <li>Create a Purchase Order for the missing items</li>
                      <li>Check availability in another warehouse</li>
                    </ul>
                  </div>
                )}

                {availability.items.some(i => !i.item_id) && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <strong>Info:</strong> Items marked "Not Linked" are not in your Parts List. 
                    Use Create PO to order these items.
                  </div>
                )}
              </div>
            ) : null}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setFulfillModalOpen(false)}>
                Cancel
              </Button>
              {availability && !availability.all_items_available && (
                <Button variant="outline" onClick={() => {
                  // PHASE 3B: Create PO prefilled with shortage items
                  const shortageItems = availability.items
                    .filter(item => !item.is_available || !item.item_id)
                    .map(item => ({
                      item_id: item.item_id || null,
                      item_name: item.item_name,
                      // For linked items with shortage: use shortage amount
                      // For unlinked items: use full requested qty (no inventory to compare)
                      quantity: item.item_id ? item.shortage : item.quantity_requested,
                      unit_cost: ''
                    }));
                  
                  const poData = {
                    warehouse_id: availability.warehouse_id,
                    job_number: fulfillingRequest?.job_number || '',
                    department_id: fulfillingRequest?.department_id || '',
                    notes: `From Part Request - Job: ${fulfillingRequest?.job_number || 'N/A'}${fulfillingRequest?.job_name ? ` - ${fulfillingRequest.job_name}` : ''}`,
                    line_items: shortageItems
                  };
                  
                  sessionStorage.setItem('prefill_po', JSON.stringify(poData));
                  setFulfillModalOpen(false);
                  navigate('/purchase-orders?prefill=true');
                  toast.info('Redirecting to PO form with shortage items...');
                }}>
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Create PO for Shortage
                </Button>
              )}
              <Button 
                onClick={handleFulfillFromInventory}
                disabled={fulfilling || !fulfillValid}
                className="bg-green-600 hover:bg-green-700"
                data-testid="fulfill-submit-btn"
              >
                {fulfilling ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Allocating...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-1" /> Allocate to Job</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Parts Used (Consume) Modal */}
        <Dialog open={consumeModalOpen} onOpenChange={setConsumeModalOpen}>
          <DialogContent className="max-w-2xl overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hammer className="w-5 h-5 text-amber-600" />
                Mark Parts Used
              </DialogTitle>
              <DialogDescription>
                Confirm actual quantities used on the job. This will reduce warehouse on-hand inventory.
              </DialogDescription>
            </DialogHeader>

            {loadingAllocations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading allocations...</span>
              </div>
            ) : allocations.length > 0 ? (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <span className="font-medium">Job:</span> {consumingRequest?.job_number} {consumingRequest?.job_name && `— ${consumingRequest.job_name}`}
                </div>

                <div className="border rounded-lg divide-y">
                  <div className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
                    <span>Item</span>
                    <span className="w-[200px] text-right">Qty to Use</span>
                  </div>
                  {allocations.map((alloc) => (
                    <div key={alloc.item_id} className="grid grid-cols-[1fr_auto] gap-3 px-3 py-3 items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{alloc.item_name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Allocated: {alloc.qty_allocated} &middot; Used: {alloc.qty_used} &middot; Remaining: {alloc.qty_remaining}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 w-[200px] justify-end shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">of {alloc.qty_remaining}</span>
                        <Input
                          type="number"
                          min={0}
                          max={alloc.qty_remaining}
                          className="w-20 h-8 text-center"
                          value={consumeQuantities[alloc.item_id] || 0}
                          onChange={(e) => {
                            const v = Math.min(Math.max(0, parseInt(e.target.value) || 0), alloc.qty_remaining);
                            setConsumeQuantities(prev => ({...prev, [alloc.item_id]: v}));
                          }}
                          data-testid={`consume-qty-${alloc.item_id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {allocations.every(a => a.qty_remaining === 0) && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-300">
                    All allocated parts have already been consumed.
                  </div>
                )}
              </div>
            ) : (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-muted-foreground">No inventory allocations found for this request.</p>
                <p className="text-xs text-muted-foreground/60">
                  This request may have been fulfilled via Purchase Order rather than from warehouse stock.
                  Parts ordered via PO do not have warehouse allocations to consume.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConsumeModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConsume}
                disabled={consuming || allocations.every(a => a.qty_remaining === 0) || !Object.values(consumeQuantities).some(v => v > 0)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {consuming ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Consuming...</>
                ) : (
                  <><Hammer className="w-4 h-4 mr-1" /> Confirm Parts Used</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Return to Inventory Modal */}
        <Dialog open={returnModalOpen} onOpenChange={setReturnModalOpen}>
          <DialogContent className="max-w-2xl overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" data-testid="return-modal-title">
                <Undo2 className="w-5 h-5 text-blue-600" />
                Return to Inventory
              </DialogTitle>
              <DialogDescription>
                Return unused or consumed parts back to warehouse stock. This will increase on-hand inventory.
              </DialogDescription>
            </DialogHeader>

            {loadingReturnAllocations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading allocations...</span>
              </div>
            ) : returnAllocations.length > 0 ? (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <span className="font-medium">Job:</span> {returningRequest?.job_number} {returningRequest?.job_name && `— ${returningRequest.job_name}`}
                </div>

                <div className="border rounded-lg divide-y">
                  {returningRequest?.status === 'Staged' ? (
                    <>
                      <div className="grid grid-cols-[1fr_100px_auto] gap-2 px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
                        <span>Item</span>
                        <span className="text-center">Staged Qty</span>
                        <span className="w-[140px] text-right">Qty to Return</span>
                      </div>
                      {returnAllocations.map((alloc) => {
                        const maxReturn = alloc.qty_allocated;
                        return (
                          <div key={alloc.item_id} className="grid grid-cols-[1fr_100px_auto] gap-2 px-3 py-3 items-center" data-testid={`return-row-${alloc.item_id}`}>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{alloc.item_name}</p>
                            </div>
                            <span className="text-sm text-center font-medium">{alloc.qty_allocated}</span>
                            <div className="flex items-center gap-2 w-[140px] justify-end shrink-0">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">max {maxReturn}</span>
                              <Input
                                type="number"
                                min={0}
                                max={maxReturn}
                                className="w-20 h-8 text-center"
                                value={returnQuantities[alloc.item_id] || 0}
                                onChange={(e) => {
                                  const v = Math.min(Math.max(0, parseInt(e.target.value) || 0), maxReturn);
                                  setReturnQuantities(prev => ({...prev, [alloc.item_id]: v}));
                                }}
                                data-testid={`return-qty-${alloc.item_id}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-[1fr_80px_80px_80px_auto] gap-2 px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
                        <span>Item</span>
                        <span className="text-center">Allocated</span>
                        <span className="text-center">Used</span>
                        <span className="text-center">Remaining</span>
                        <span className="w-[140px] text-right">Qty to Return</span>
                      </div>
                      {returnAllocations.map((alloc) => {
                        const maxReturn = alloc.qty_remaining + alloc.qty_used;
                        return (
                          <div key={alloc.item_id} className="grid grid-cols-[1fr_80px_80px_80px_auto] gap-2 px-3 py-3 items-center" data-testid={`return-row-${alloc.item_id}`}>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{alloc.item_name}</p>
                            </div>
                            <span className="text-sm text-center">{alloc.qty_allocated}</span>
                            <span className="text-sm text-center">{alloc.qty_used}</span>
                            <span className="text-sm text-center">{alloc.qty_remaining}</span>
                            <div className="flex items-center gap-2 w-[140px] justify-end shrink-0">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">max {maxReturn}</span>
                              <Input
                                type="number"
                                min={0}
                                max={maxReturn}
                                className="w-20 h-8 text-center"
                                value={returnQuantities[alloc.item_id] || 0}
                                onChange={(e) => {
                                  const v = Math.min(Math.max(0, parseInt(e.target.value) || 0), maxReturn);
                                  setReturnQuantities(prev => ({...prev, [alloc.item_id]: v}));
                                }}
                                data-testid={`return-qty-${alloc.item_id}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="return-reason" className="text-sm">Reason for return (optional)</Label>
                  <Textarea
                    id="return-reason"
                    placeholder="e.g. Job scope reduced, extra parts not needed..."
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    className="resize-none h-20"
                    data-testid="return-reason-input"
                  />
                </div>
              </div>
            ) : (
              <div className="py-6 text-center space-y-2">
                <p className="text-sm text-muted-foreground">No returnable allocations found.</p>
                <p className="text-xs text-muted-foreground/60">
                  All parts have either been fully returned already, or there are no allocations to return.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReturnModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleReturn}
                disabled={returning || returnAllocations.length === 0 || !Object.values(returnQuantities).some(v => v > 0)}
                data-testid="confirm-return-btn"
              >
                {returning ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Returning...</>
                ) : (
                  <><Undo2 className="w-4 h-4 mr-1" /> Confirm Return</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Convert to PO Confirmation */}
        <AlertDialog open={convertModalOpen} onOpenChange={setConvertModalOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Convert to Purchase Order</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the part request as <strong>Fulfilled</strong> and open the Purchase Order form 
                pre-filled with the request details.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConvertToPO}>
                <ArrowRight className="w-4 h-4 mr-2" />
                Continue to PO
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 6B: Stage Parts Modal */}
        <Dialog open={stageModalOpen} onOpenChange={setStageModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Stage Parts
              </DialogTitle>
              <DialogDescription>
                Mark parts as physically staged/picked for the job.
                {stagingRequest?.job_number && (
                  <span className="block mt-1 font-medium">
                    Job: {stagingRequest.job_number} {stagingRequest.job_name && `- ${stagingRequest.job_name}`}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {stagingRequest && (
              <div className="py-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    Enter quantities staged for each item:
                  </span>
                  <Button variant="outline" size="sm" onClick={handleStageAll}>
                    Stage All
                  </Button>
                </div>
                
                <div className="border rounded divide-y max-h-[300px] overflow-y-auto">
                  {stagingRequest.line_items.map((item) => {
                    const stagedQty = stagingQuantities[item.id] || 0;
                    const fullyStaged = stagedQty >= item.quantity_requested;
                    
                    return (
                      <div key={item.id} className={`p-3 ${fullyStaged ? 'bg-green-50' : ''}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                            {item.item_part_number && (
                              <p className="text-xs text-muted-foreground">
                                Part #: {item.item_part_number}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-sm">
                            <span className="text-muted-foreground">Requested:</span>{' '}
                            <span className="font-medium">{item.quantity_requested}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Label className="text-sm whitespace-nowrap">Staged:</Label>
                          <IntegerQuantityInput
                            min="0"
                            max={item.quantity_requested}
                            value={stagedQty}
                            onChange={(val) => handleStagingQuantityChange(item.id, val)}
                            className="w-24"
                            data-testid={`stage-qty-${item.id}`}
                          />
                          <span className="text-sm text-muted-foreground">
                            / {item.quantity_requested}
                          </span>
                          {fullyStaged && (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Summary */}
                <div className="bg-muted/50 p-3 rounded text-sm">
                  <div className="flex justify-between">
                    <span>Items fully staged:</span>
                    <span className="font-medium">
                      {stagingRequest.line_items.filter(li => 
                        (stagingQuantities[li.id] || 0) >= li.quantity_requested
                      ).length} / {stagingRequest.line_items.length}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setStageModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveStaging} disabled={stagingSaving}>
                {stagingSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Staging
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unstage confirmation dialog */}
        <Dialog open={unstageModalOpen} onOpenChange={(o) => { if (!o) { setUnstageModalOpen(false); setUnstagingRequest(null); } }}>
          <DialogContent className="max-w-md" data-testid="unstage-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-amber-600" />
                {unstagingRequest?.status === 'Staged' ? 'Unstage Part Request' : 'Clear Staged Quantities'}
              </DialogTitle>
              <DialogDescription>
                This will reverse the staging operation and restore the related inventory quantities. Continue?
              </DialogDescription>
            </DialogHeader>

            {unstagingRequest && (
              <div className="space-y-3 py-2">
                <div className="rounded border bg-muted/40 p-3 text-sm space-y-1">
                  <div><span className="text-muted-foreground">Job:</span> <span className="font-medium">{unstagingRequest.job_number || '—'}</span>{unstagingRequest.job_name ? ` · ${unstagingRequest.job_name}` : ''}</div>
                  <div><span className="text-muted-foreground">Warehouse:</span> <span className="font-medium">{unstagingRequest.warehouse_name || '—'}</span></div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>{' '}
                    <span className="font-medium">{unstagingRequest.status}</span>
                    {' '}<ArrowRight className="inline w-3 h-3 mx-1 text-muted-foreground" />{' '}
                    <span className="font-medium">Approved</span>
                  </div>
                </div>

                {Array.isArray(unstagingRequest.line_items) && unstagingRequest.line_items.some(li => (li.quantity_staged || 0) > 0) && (
                  <div className="rounded border divide-y text-sm" data-testid="unstage-preview-lines">
                    <div className="px-3 py-1.5 text-xs uppercase tracking-wide text-muted-foreground bg-muted/30">
                      Quantities to clear
                    </div>
                    {unstagingRequest.line_items.filter(li => (li.quantity_staged || 0) > 0).map((li) => (
                      <div key={li.id} className="px-3 py-2 flex justify-between items-center">
                        <span className="truncate">{li.item_name}</span>
                        <span className="ml-2 text-muted-foreground">
                          {li.quantity_staged} <ArrowRight className="inline w-3 h-3 mx-1" /> 0
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="rounded border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-900">
                  Soft reservations on warehouse inventory remain in place — the request returns to Approved and remains active.
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Reason (optional)</Label>
                  <Select value={unstageReason} onValueChange={setUnstageReason}>
                    <SelectTrigger data-testid="unstage-reason-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staged_by_mistake">Staged by mistake</SelectItem>
                      <SelectItem value="wrong_job">Wrong job</SelectItem>
                      <SelectItem value="wrong_warehouse">Wrong warehouse</SelectItem>
                      <SelectItem value="request_changed">Request changed</SelectItem>
                      <SelectItem value="duplicate_request">Duplicate request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {unstageReason === 'other' && (
                    <Textarea
                      placeholder="Describe the reason (optional, max 500 chars)"
                      value={unstageOtherText}
                      onChange={(e) => setUnstageOtherText(e.target.value.slice(0, 500))}
                      rows={2}
                      data-testid="unstage-reason-other"
                    />
                  )}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setUnstageModalOpen(false); setUnstagingRequest(null); }} disabled={unstaging} data-testid="unstage-cancel-btn">
                Cancel
              </Button>
              <Button onClick={handleConfirmUnstage} disabled={unstaging} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="unstage-confirm-btn">
                {unstaging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                Confirm Unstage
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>

      {/* Mobile FAB */}
      {canCreateRequest && (
        <MobileFABContainer data-testid="part-requests-fab-container">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); openCreateModal(); }}>
              New Request
            </MobileFABMenuItem>
          </MobileFABMenu>
          <button
            className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
            onClick={() => setFabOpen(!fabOpen)}
            data-testid="part-requests-mobile-fab"
          >
            <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
          </button>
        </MobileFABContainer>
      )}
      <AvailabilityBreakdownModal open={breakdownState.open} onOpenChange={setBreakdownOpen} data={breakdownState.data} loading={breakdownState.loading} />
    </Layout>
  );
};
